import React from 'react';

import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Group} from '@pb/kit-component-group';
import {hashedClasses} from '@pb/kit-util-with-css-module';

import {FlatButton} from '../flat-button/flat-button.component';
import styles from './dropdown-compound-button.component.module.styl';

import type {FlatButtonProps} from '../flat-button/flat-button.component';


const classes = hashedClasses(styles);

export interface DropdownCompoundButtonProps {
    /**
     * Props forwarded to the primary (main-action) `FlatButton`. The button's
     * label is automatically wrapped in `<strong>` for bold text.
     */
    primaryButton?: FlatButtonProps,
    /**
     * Props forwarded to the secondary (dropdown-trigger) `FlatButton`. The
     * button defaults to a downward chevron icon (`Icons.ANGLE_DOWN`) and
     * `aria-label="Open"` — override either via this prop.
     */
    secondaryButton?: FlatButtonProps,
}

/**
 * Two-button combination for one-click primary actions with a secondary
 * dropdown trigger (e.g. "Download" + "▼ More formats").
 *
 * The primary button has bold label text by default. The secondary button
 * renders a down-arrow icon and is separated from the primary by a divider
 * line. Wire the secondary button's `onClick` to open a `Dropdown` or similar
 * overlay with additional options.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-dropdown-compound-button--documentation}
 */
export function DropdownCompoundButton(props: DropdownCompoundButtonProps) {
    const {secondaryButton, ...compoundButtonProps} = props;

    const secondaryButtonProps = {
        'children': <Icon type={Icons.ANGLE_DOWN} size={Sizes.Size_14}/>,
        'aria-label': 'Open',
        'className': classes['dropdown-compound-secondary-button'],
        ...secondaryButton,
    };

    return (
        <Group gap="none">
            <FlatButton
                {...compoundButtonProps.primaryButton}>
                <strong>
                    {compoundButtonProps.primaryButton?.children}
                </strong>
            </FlatButton>
            <div className={classes.divider}/>
            <FlatButton {...secondaryButtonProps}/>
        </Group>
    );
}
